import css from "./heroimage.scss?inline";
import globalStyles from "../../index.scss?inline";
import { ArgSpecDictionary } from "../component-utils";
import { Component } from "../../utils/Component";

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = "HHDSHeroImage";
export const HHDSHeroImageTagName: string = "hhds-hero-image";
//const TAG_NAME: string = HHDSHeroImageTagName;

const USE_HHDS_IMAGE: boolean = false;

enum HorizontalLayoutType {
  fullwidth = "fullwidth",
  fullbleed = "fullbleed",
  margins = "margins",
}

export const HHDSHeroImageAttrNames = {
  src: "src",
  alt: "alt",
  overlay: "overlay",
  overlayAlt: "overlay-alt",
  cover: "cover",
  modalSrc: "modal-src",
  modalCover: "modal-cover",
  directVideo: "direct-video",
  dynamicCursor: "dynamic-cursor",
  horizontalLayout: "horizontal-layout",
};

const Attrs = HHDSHeroImageAttrNames;

export class HHDSHeroImage extends Component {
  constructor() {
    super();
  }

  protected override init(): void {
    const src = this.vars.get<string>(Attrs.src);
    const alt = this.vars.get<string>(Attrs.alt);

    const overlay_src = this.vars.get<string>(Attrs.overlay);
    const overlay_alt = this.vars.get<string>(Attrs.overlayAlt);

    const horizontalLayoutType = this.vars.get<HorizontalLayoutType>(Attrs.horizontalLayout);
    switch (horizontalLayoutType) {
      case HorizontalLayoutType.fullwidth:
        this.style.setProperty("--hero-max-width", "1920px");
        break;
      case HorizontalLayoutType.fullbleed:
        this.style.setProperty("--hero-max-width", "none");
        break;
      case HorizontalLayoutType.margins:
        this.style.setProperty("--hero-height-lg", "unset");
        break;
    }

    this.shadow.innerHTML = /* html */ `
    <style>${globalStyles}</style>
    <style>${css}</style>
    <div id="container" class="container">
      <div id="grid" class="grid">
        <div id="grid-inner" class="col-span-12 md:col-span-12">
          <div class="hhds-hero-image ">
            ${this.getImageHtml(src, alt)}
            ${this.getOverlayImageHtml(overlay_src, overlay_alt)}
            <div class="bottom-anchor"></div>
          </div>
        </div>
      </div>
    </div>
    `;

    if (horizontalLayoutType == HorizontalLayoutType.margins) {
      this.observeBreakpointChanges((breakpoint: string) => this.updateForBreakpoint(breakpoint));
    }
    this.updateForBreakpoint(this.currentBreakpoint);

    this.animateElementsIn();
  }

  private animateElementsIn(): void {
    const image = this.shadow.querySelector(".img") as HTMLElement;
    const transitionDuration: number = 300;
    image.style.opacity = "0";
    image.style.transition = `opacity ${transitionDuration}ms ease-out`;
    let overlay: HTMLImageElement | null = null;
    if (this.vars.get<string>(Attrs.overlay)?.length > 0) {
      overlay = this.shadow.querySelector(".hhds-hero-image__overlay .img") as HTMLImageElement;
      overlay.style.opacity = "0";
      overlay.style.transition = `opacity ${transitionDuration}ms ease-out`;
    }

    image?.addEventListener("load", () => {
      image.style.opacity = "1";
      window.setTimeout(() => {
        if (overlay) {
          if (overlay.complete && overlay.naturalWidth > 0) {
            overlay.style.opacity = "1";
          } else {
            overlay?.addEventListener("load", () => {
              overlay.style.opacity = "1";
            });
          }
        }
      }, transitionDuration);
    });
  }

  private updateForBreakpoint(breakpoint: string | null = null) {
    const horizontalLayoutType = this.vars.get<HorizontalLayoutType>(Attrs.horizontalLayout);
    const margins = horizontalLayoutType == HorizontalLayoutType.margins;
    const useGrid = breakpoint != "xs" && breakpoint != "sm" && margins;
    const container = this.shadow.querySelector("#container") as HTMLElement;
    container.style.width = "100%";
    const grid = this.shadow.querySelector("#grid");
    const gridInner = this.shadow.querySelector("#grid-inner");
    container?.classList.toggle("container", useGrid);
    grid?.classList.toggle("grid", useGrid);
    gridInner?.classList.toggle("col-span-12", useGrid);
    gridInner?.classList.toggle("md:col-span-12", useGrid);

    gridInner?.classList.toggle("hhds-hero--ignore", !useGrid);
    grid?.classList.toggle("hhds-hero--ignore", !useGrid);
    container?.classList.toggle("hhds-hero--ignore", !useGrid);
    console.log("updateForBreakpoint", breakpoint, container);
  }

  protected override destroy(): void {
    DEBUG_VERBOSE && console.log(CLASS_NAME, "destroy");
  }

  override onAttributeChanged(name: string, _oldValue: string, newValue: string): void {
    DEBUG_VERBOSE && console.log(CLASS_NAME, "Attribute changed: ", name, _oldValue, newValue);
    this.reinit();
  }

  override onSlotChange(_slot: HTMLSlotElement, elements: Element[]): void {
    if (elements.length == 0) {
      DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot emptied");
    } else {
      DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot changed");
    }
  }

  get bottomAnchor(): HTMLElement {
    return this.shadow.querySelector(".bottom-anchor") as HTMLElement;
  }

  static override argSpecs(): ArgSpecDictionary {
    return ArgSpecs;
  }

  private getOverlayImageHtml(src: string, alt: string): string {
    if (!src) {
      return "";
    }

    return `<div class="hhds-hero-image__overlay">
      <img class="img" src="${src}" alt="${alt}"></img></div>`;
  }

  private getImageHtml(src: string, alt: string): string {
    if (USE_HHDS_IMAGE) {
      const style: string = `
      --image-object-fit: cover;
      --image-width: 100%;
      --image-height: 100%`;
      return `<hhds-image src="${src}"
        ${this.getAttribute("srcSet") !== null ? `srcset="${this.getAttribute("srcSet")}` : ``}"
        ${this.getAttribute("sizes") !== null ? `sizes="${this.getAttribute("sizes")}` : ``}"
        alt="${alt}"
        style="${style}"></hhds-image>`;
    } else {
      return `<div class="image-container"><img class="img"
        ${this.getAttribute("srcSet") !== null ? `srcset="${this.getAttribute("srcSet")}` : ``}"
        ${this.getAttribute("sizes") !== null ? `sizes="${this.getAttribute("sizes")}` : ``}"
        src="${src}" alt="${alt}"></img></div>`;
    }
  }
}

export const ArgSpecs: ArgSpecDictionary = {
  [Attrs.overlay]: {
    description: "URL of overlay image.",
    defaultValue: "",
    type: String,
  },
  [Attrs.overlayAlt]: {
    description: "Alt text for the overlay image.",
    defaultValue: "",
    type: String,
  },
  [Attrs.horizontalLayout]: {
    description: "Type of horizontal layout to use",
    defaultValue: HorizontalLayoutType.fullwidth,
    typeString: "HorizontalLayoutType",
    type: HorizontalLayoutType,
  },
  [Attrs.src]: {
    description: "URL to an image or a Vimeo-hosted video.",
    defaultValue: "",
    type: String,
  },
  [Attrs.alt]: {
    description: "Alt text for the image.",
    defaultValue: "",
    type: String,
  },
  [Attrs.cover]: {
    description: "The URL of the cover image, if applicable.",
    defaultValue: "",
    type: String,
  },
  [Attrs.modalSrc]: {
    description: "URL to a Vimeo-hosted video that will open in a modal when the hero is activated.",
    defaultValue: "",
    type: String,
  },
  [Attrs.modalCover]: {
    description: "The URL of the cover image for the modal, if applicable.",
    defaultValue: "",
    type: String,
  },
  [Attrs.directVideo]: {
    description: "Whether to use direct video instead of the Vimeo player for the Hero's initial video.",
    defaultValue: true,
    type: Boolean,
  },
  [Attrs.dynamicCursor]: {
    description: "Whether to use a dynamic cursor for the Hero.",
    defaultValue: true,
    type: Boolean,
  },
};
